import { useCallback, useEffect, useState } from 'react';
import { DeviceCreate } from '../../api/models';
import { deviceService, DeviceDetail } from '../../services/deviceService';
import { ConfigTemplate } from '../../types/configTemplate';
import { typesService, HardwareType } from '../../services/typesService';
import { CONFIG_FIELDS } from '../../config/configSchema';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useRawTabVisible } from '../../hooks/useRawTabVisible';

export type DeviceDetailTab =
  | 'basic' | 'settings' | 'raw' | 'v2conf' | 'history'
  | 'monitoring' | 'programming' | 'events';


export const useDeviceDetail = ( device: DeviceCreate | null, isOpen: boolean,onDeviceUpdated?: (device: DeviceCreate) => void) => {
  const [activeTab, setActiveTab] = useState<DeviceDetailTab>('basic');
  const { canViewAuditLog } = useClientAccess();
  const rawTabVisible = useRawTabVisible();
  const [error, setError] = useState<string | null>(null);
  const [deviceDetail, setDeviceDetail] = useState<DeviceDetail | null>(null);
  const [template, setTemplate] = useState<ConfigTemplate | null>(null);
  const [hardwareTypes, setHardwareTypes] = useState<HardwareType[]>([]);
  const fieldDefinitions = CONFIG_FIELDS;

  useEffect(() => {
    if (rawTabVisible === false && activeTab === 'raw') {
      setActiveTab('basic');
    }
  }, [rawTabVisible, activeTab]);


  useEffect(() => {
    if (!isOpen) return;
    typesService.getHardwareTypes(false).then(setHardwareTypes).catch(console.error);
  }, [isOpen]);

  const reloadDetail = useCallback(async (): Promise<DeviceDetail | null> => {
    if (!device?.idDevice) return null;
    try {
      const detail = await deviceService.getDeviceDetail(device.idDevice);
      setDeviceDetail(detail);
      return detail;
    } catch (err) {
      console.error('Failed to load device detail:', err);
      return null;
    }
  }, [device?.idDevice]);

  
  useEffect(() => {
    if (!isOpen || !device?.idDevice) return;
    setActiveTab('basic');
    setError(null);
    setDeviceDetail(null);
    reloadDetail();
  }, [isOpen, device?.idDevice]);

  
  useEffect(() => {
    const id = deviceDetail?.template_id;
    if (!isOpen || id == null) {
      setTemplate(null);
      return;
    }
    let cancelled = false;
    deviceService.getTemplate(id)
      .then((tpl) => { if (!cancelled) setTemplate(tpl); })
      .catch((err) => { console.error('Failed to load template:', err); if (!cancelled) setTemplate(null); });
    return () => { cancelled = true; };
  }, [isOpen, deviceDetail?.template_id]);

  
  return {
    device,
    isOpen,
    activeTab,
    setActiveTab,
    canViewAuditLog,
    rawTabVisible,
    error,
    setError,
    deviceDetail,
    setDeviceDetail,
    reloadDetail,
    template,
    hardwareTypes,
    fieldDefinitions,
    onDeviceUpdated,
  };
};


export type DeviceDetailCore = ReturnType<typeof useDeviceDetail>;
